import React, { useEffect, useMemo, useState } from 'react';
import {
  Paper,
  Typography,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  Switch,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, List as ListIcon } from '@mui/icons-material';
import axios from 'axios';

interface FeeHead {
  id: string;
  name: string;
  code: string;
  type: 'REGULAR' | 'ONE_TIME' | 'FINE' | 'DISCOUNT';
  displayOrder: number;
  isRefundable: boolean;
  isDiscountApplicable: boolean;
  isActive: boolean;
}

const FeeHeadMaster: React.FC = () => {
  const [items, setItems] = useState<FeeHead[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reorderOpen, setReorderOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<FeeHead | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    type: 'REGULAR',
    isRefundable: false,
    isDiscountApplicable: false,
    isActive: true,
  });
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0);

  const [reorderState, setReorderState] = useState<{ id: string; displayOrder: number; name: string }[]>([]);

  useEffect(() => {
    fetchItems();
  }, [page, pageSize, search]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const skip = page * pageSize;
      const { data } = await axios.get('/system-settings/fee-head', {
        params: { search, skip, take: pageSize },
      });
      setItems(data.items);
    } catch (error) {
      console.error('Failed to fetch fee heads:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = useMemo(() => items, [items]);

  const handleCreate = () => {
    setEditingItem(null);
    setFormData({
      name: '',
      code: '',
      type: 'REGULAR',
      isRefundable: false,
      isDiscountApplicable: false,
      isActive: true,
    });
    setDialogOpen(true);
  };

  const handleEdit = (item: FeeHead) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      code: item.code,
      type: item.type as any,
      isRefundable: item.isRefundable,
      isDiscountApplicable: item.isDiscountApplicable,
      isActive: item.isActive,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const payload = { ...formData };
      if (editingItem) {
        await axios.patch(`/system-settings/fee-head/${editingItem.id}`, payload);
      } else {
        await axios.post('/system-settings/fee-head', payload);
      }
      setDialogOpen(false);
      await fetchItems();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to save fee head');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this fee head?')) {
      try {
        await axios.delete(`/system-settings/fee-head/${id}`);
        await fetchItems();
      } catch (error: any) {
        alert(error.response?.data?.message || 'Cannot delete fee head. It may be referenced.');
      }
    }
  };

  const handleToggleStatus = async (id: string, currentStatus: boolean) => {
    try {
      await axios.patch(`/system-settings/fee-head/${id}/toggle-status`);
      setItems((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, isActive: !currentStatus } : item
        )
      );
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to toggle status');
      fetchItems();
    }
  };

  const openReorder = () => {
    setReorderState(
      [...items]
        .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
        .map((i) => ({ id: i.id, displayOrder: i.displayOrder ?? 0, name: i.name })),
    );
    setReorderOpen(true);
  };

  const applyReorder = async () => {
    try {
      const orderedIds = [...reorderState]
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map((x) => x.id);
      await axios.patch('/system-settings/fee-head/reorder', { order: orderedIds });
      setReorderOpen(false);
      await fetchItems();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to save order');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Fee Heads</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<ListIcon />} onClick={openReorder}>
            Change Order
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
            Create
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <TextField
          label="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <InputLabel id="show-entries-label">Show entries</InputLabel>
          <Select
            labelId="show-entries-label"
            value={pageSize}
            size="small"
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Fee Head Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={2} align="center">
                  No fee heads found
                </TableCell>
              </TableRow>
            ) : (
              filteredItems.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>
                    <Switch
                      checked={item.isActive}
                      onChange={() => handleToggleStatus(item.id, item.isActive)}
                      color="primary"
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" title="View" onClick={() => alert(`Name: ${item.name}`)}>
                      <ListIcon />
                    </IconButton>
                    <IconButton size="small" title="Edit" onClick={() => handleEdit(item)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" title="Delete" onClick={() => handleDelete(item.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? 'Edit Fee Head' : 'Create Fee Head'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Fee Head Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              fullWidth
              required
            />
            <TextField
              select
              label="Type"
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
              fullWidth
              required
            >
              <MenuItem value="REGULAR">Regular</MenuItem>
              <MenuItem value="ONE_TIME">One Time</MenuItem>
              <MenuItem value="FINE">Fine</MenuItem>
              <MenuItem value="LATE_FINE">Late Fine</MenuItem>
              <MenuItem value="DISCOUNT">Discount</MenuItem>
              <MenuItem value="OTHER">Other</MenuItem>
            </TextField>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isRefundable}
                  onChange={(e) => setFormData({ ...formData, isRefundable: e.target.checked })}
                />
              }
              label="Is Refundable"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isDiscountApplicable}
                  onChange={(e) =>
                    setFormData({ ...formData, isDiscountApplicable: e.target.checked })
                  }
                />
              }
              label="Is Discount Applicable"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reorderOpen} onClose={() => setReorderOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Order</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {reorderState.map((row, idx) => (
              <Box key={row.id} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                  label="Order"
                  type="number"
                  value={row.displayOrder}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    setReorderState((prev) =>
                      prev.map((r, i) => (i === idx ? { ...r, displayOrder: val } : r)),
                    );
                  }}
                  sx={{ width: 90 }}
                />
                <Typography>{row.name}</Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReorderOpen(false)}>Cancel</Button>
          <Button onClick={applyReorder} variant="contained">
            Save Order
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FeeHeadMaster;

